'use client';

import { useEffect, useId, useRef, useState, type ReactNode, type UIEventHandler } from 'react';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import Image from 'next/image';

import styles from './PolicyModalShell.module.css';

type PolicyModalShellProps = {
  alreadyAgreed?: boolean;
  children: ReactNode;
  isOpen: boolean;
  title: string;
  onAgree?: () => void;
  onClose: () => void;
};

export function PolicyModalShell(props: PolicyModalShellProps) {
  if (!props.isOpen) return null;

  return <OpenPolicyModal {...props} />;
}

function OpenPolicyModal({ alreadyAgreed = false, onAgree, onClose, title, children }: PolicyModalShellProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [hasReadAll, setHasReadAll] = useState(false);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const body = bodyRef.current;
      setHasReadAll(Boolean(body && body.scrollHeight <= body.clientHeight + 10));
    });
    return () => window.cancelAnimationFrame(frameId);
  }, []);

  const handleScroll: UIEventHandler<HTMLDivElement> = event => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      setHasReadAll(true);
    }
  };

  const canAgree = Boolean(onAgree) && hasReadAll && !alreadyAgreed;

  return (
    <div className={styles.overlay} data-policy-modal-open onClick={onClose} role="presentation">
      <div className={styles.stage}>
        <section className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={event => event.stopPropagation()}>
          <button type="button" onClick={onClose} className={styles.closeButton} aria-label="닫기">
            <Image src="/images/icons/policy-close.svg" alt="" width={22} height={22} aria-hidden="true" />
          </button>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <ScrollAreaPrimitive.Root className={styles.body} type="always">
            <ScrollAreaPrimitive.Viewport ref={bodyRef} className={`${styles.viewport} ${!hasReadAll ? styles.bodyUnread : ''}`} onScroll={handleScroll}>
              <div className={styles.prose}>{children}</div>
            </ScrollAreaPrimitive.Viewport>
            <ScrollAreaPrimitive.Scrollbar orientation="vertical" className={styles.scrollbar} style={{ right: 'calc(-1 * var(--policy-dialog-padding) + 4px)' }}>
              <ScrollAreaPrimitive.Thumb className={styles.scrollThumb} />
            </ScrollAreaPrimitive.Scrollbar>
          </ScrollAreaPrimitive.Root>
          {onAgree ? (
            <div className={styles.footer}>
              <button type="button" onClick={onAgree} disabled={!canAgree} className={canAgree ? styles.agreeButton : styles.agreeButtonDisabled}>
                {alreadyAgreed ? '이미 동의하셨습니다' : '동의하기'}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
